// ── Shared Field Schema per purchaseType ─────────────────────────────────────
// Used by NeedsAIAssistant.jsx and OptionsManagement.jsx

const FIELD_SCHEMAS = {
  'computers-laptops': {
    label: 'Computer / Laptop',
    required: ['deviceType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'deviceType', label: 'Device Type', hint: 'laptop, desktop, all-in-one, workstation' },
      { id: 'processor', label: 'Processor', hint: 'e.g. Intel Core i7, AMD Ryzen 9' },
      { id: 'ram', label: 'RAM', hint: 'e.g. 16GB, 32GB' },
      { id: 'storage', label: 'Storage', hint: 'e.g. 512GB SSD' },
      { id: 'screenSize', label: 'Screen Size', hint: 'e.g. 15.6 inch' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1500' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Apple, Dell' },
      { id: 'operatingSystem', label: 'Operating System', hint: 'e.g. Windows 11, macOS' },
    ],
  },
  'tablets': {
    label: 'Tablet',
    required: ['operatingSystem', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'operatingSystem', label: 'Operating System', hint: 'iOS, Android, Windows' },
      { id: 'screenSize', label: 'Screen Size', hint: 'e.g. 10.9 inch' },
      { id: 'storage', label: 'Storage', hint: 'e.g. 128GB' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $600' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Apple iPad' },
      { id: 'useCase', label: 'Use Case', hint: 'e.g. drawing, school, streaming' },
    ],
  },
  'smartphones': {
    label: 'Smartphone',
    required: ['operatingSystem', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'operatingSystem', label: 'Operating System', hint: 'iOS, Android' },
      { id: 'storage', label: 'Storage', hint: 'e.g. 128GB' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $800' },
      { id: 'brandPreference', label: 'Brand / Model', hint: 'e.g. iPhone 15 Pro' },
      { id: 'carrier', label: 'Carrier', hint: 'e.g. Verizon, Unlocked' },
      { id: 'colorPreference', label: 'Color', hint: 'e.g. Black, White' },
    ],
  },
  'tv-home-theater': {
    label: 'TV / Home Theater',
    required: ['screenSize', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'screenSize', label: 'Screen Size', hint: 'e.g. 65 inch' },
      { id: 'displayType', label: 'Display Type', hint: 'OLED, QLED, LED' },
      { id: 'resolution', label: 'Resolution', hint: '4K, 8K, 1080p' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1200' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Samsung, LG, Sony' },
      { id: 'smartFeatures', label: 'Smart Features', hint: 'e.g. built-in streaming' },
    ],
  },
  'smart-home-devices': {
    label: 'Smart Home Device',
    required: ['deviceType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'deviceType', label: 'Device Type', hint: 'e.g. thermostat, doorbell, hub' },
      { id: 'ecosystem', label: 'Ecosystem', hint: 'Alexa, Google Home, HomeKit' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $200' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Nest, Ring' },
      { id: 'installationRequirements', label: 'Installation', hint: 'DIY or professional' },
    ],
  },
  'audio-equipment': {
    label: 'Audio Equipment',
    required: ['audioType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'audioType', label: 'Audio Type', hint: 'headphones, earbuds, soundbar, home theater' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $300' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Bose, Sony, Sonos' },
      { id: 'connectivity', label: 'Connectivity', hint: 'Bluetooth, Wi-Fi, wired' },
      { id: 'useCase', label: 'Use Case', hint: 'music, gaming, home theater' },
    ],
  },
  'wearables': {
    label: 'Wearable',
    required: ['wearableType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'wearableType', label: 'Wearable Type', hint: 'smartwatch, fitness tracker, VR headset' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $400' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Apple Watch, Fitbit' },
      { id: 'compatibleDevice', label: 'Compatible Device', hint: 'iPhone, Android' },
      { id: 'keyFeatures', label: 'Key Features', hint: 'e.g. heart rate, GPS, sleep tracking' },
    ],
  },
  'home-purchase': {
    label: 'Home Purchase',
    required: ['purchasePrice', 'desiredLocation'],
    fields: [
      { id: 'purchasePrice', label: 'Target Price (not shared with vendors)', hint: 'e.g. $450,000' },
      { id: 'downPayment', label: 'Down Payment (not shared with vendors)', hint: 'e.g. $90,000 (20%)' },
      { id: 'loanType', label: 'Loan Type', hint: 'conventional, FHA, VA, USDA' },
      { id: 'creditScore', label: 'Credit Score (not shared with vendors)', hint: 'e.g. 720-740' },
      { id: 'desiredLocation', label: 'Location', hint: 'e.g. Austin, TX' },
      { id: 'bedroomsBathrooms', label: 'Beds/Baths', hint: 'e.g. 3 bed / 2 bath' },
      { id: 'squareFootage', label: 'Square Footage', hint: 'e.g. 1,800 sq ft' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. 30-60 days' },
    ],
  },
  'home-refinance': {
    label: 'Home Refinance',
    required: ['currentLoanBalance', 'currentInterestRate', 'refinanceGoal'],
    fields: [
      { id: 'currentLoanBalance', label: 'Current Loan Balance (not shared with vendors)', hint: 'e.g. $320,000' },
      { id: 'currentInterestRate', label: 'Current Rate', hint: 'e.g. 6.75%' },
      { id: 'refinanceGoal', label: 'Refinance Goal', hint: 'lower rate, cash-out, shorten term' },
      { id: 'homeValue', label: 'Home Value (not shared with vendors)', hint: 'e.g. $500,000' },
      { id: 'creditScore', label: 'Credit Score (not shared with vendors)', hint: 'e.g. 740-760' },
      { id: 'cashOutAmount', label: 'Cash-Out Amount (not shared with vendors)', hint: 'e.g. $50,000' },
    ],
  },
  'roofing': {
    label: 'Roofing',
    required: ['projectType', 'priceMax'],
    fields: [
      { id: 'projectType', label: 'Project Type', hint: 'full replacement, repair, inspection' },
      { id: 'roofingMaterial', label: 'Material', hint: 'asphalt shingles, metal, tile' },
      { id: 'roofSize', label: 'Roof Size', hint: 'e.g. 2,000 sq ft' },
      { id: 'stories', label: 'Stories', hint: 'e.g. 1, 2' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $12,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 30 days' },
    ],
  },
  'hvac': {
    label: 'HVAC',
    required: ['serviceType', 'systemType', 'priceMax'],
    fields: [
      { id: 'serviceType', label: 'Service Type', hint: 'installation, replacement, repair, maintenance' },
      { id: 'systemType', label: 'System Type', hint: 'central AC, heat pump, furnace, mini split' },
      { id: 'homeSize', label: 'Home Size', hint: 'e.g. 2,500 sq ft' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $8,000' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Carrier, Trane' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 2 weeks' },
    ],
  },
  'plumbing': {
    label: 'Plumbing',
    required: ['issueDescription', 'priceMax'],
    fields: [
      { id: 'issueDescription', label: 'Issue / Project', hint: 'e.g. leaking pipe, water heater replacement' },
      { id: 'location', label: 'Location in Home', hint: 'e.g. master bathroom, kitchen' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $2,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. urgent, within a week' },
      { id: 'homeAge', label: 'Home Age', hint: 'e.g. built in 1985' },
    ],
  },
  'electrical': {
    label: 'Electrical',
    required: ['projectDescription', 'priceMax'],
    fields: [
      { id: 'projectDescription', label: 'Project Description', hint: 'e.g. panel upgrade, EV charger' },
      { id: 'panelAmperage', label: 'Panel Amperage', hint: 'e.g. 200A, 100A' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $3,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 2 weeks' },
      { id: 'permitsRequired', label: 'Permits', hint: 'yes, no, unknown' },
    ],
  },
  'painting': {
    label: 'Painting',
    required: ['paintingType', 'priceMax'],
    fields: [
      { id: 'paintingType', label: 'Painting Type', hint: 'interior, exterior, both' },
      { id: 'areaSqFt', label: 'Area (sq ft)', hint: 'e.g. 1,500 sq ft' },
      { id: 'numberOfRooms', label: 'Number of Rooms', hint: 'e.g. 4 rooms' },
      { id: 'colorPreferences', label: 'Color Preferences', hint: 'e.g. neutral tones' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $4,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 3 weeks' },
    ],
  },
  'flooring': {
    label: 'Flooring',
    required: ['flooringMaterial', 'areaSqFt', 'priceMax'],
    fields: [
      { id: 'flooringMaterial', label: 'Material', hint: 'hardwood, laminate, vinyl plank, tile, carpet' },
      { id: 'areaSqFt', label: 'Area (sq ft)', hint: 'e.g. 800 sq ft' },
      { id: 'numberOfRooms', label: 'Number of Rooms', hint: 'e.g. 3 rooms' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $5,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 4 weeks' },
      { id: 'subfloorCondition', label: 'Subfloor', hint: 'concrete, plywood, existing flooring' },
    ],
  },
  'windows-doors': {
    label: 'Windows & Doors',
    required: ['projectType', 'numberOfUnits', 'priceMax'],
    fields: [
      { id: 'projectType', label: 'Project Type', hint: 'windows, doors, both' },
      { id: 'numberOfUnits', label: 'Number of Units', hint: 'e.g. 8 windows, 2 doors' },
      { id: 'dimensions', label: 'Dimensions', hint: 'e.g. 36x48 inches' },
      { id: 'material', label: 'Material', hint: 'vinyl, wood, fiberglass, aluminum' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $6,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 6 weeks' },
    ],
  },
  'fencing': {
    label: 'Fencing',
    required: ['fenceMaterial', 'linearFootage', 'priceMax'],
    fields: [
      { id: 'fenceMaterial', label: 'Material', hint: 'wood, vinyl, chain link, aluminum' },
      { id: 'linearFootage', label: 'Linear Footage', hint: 'e.g. 200 linear feet' },
      { id: 'fenceHeight', label: 'Height', hint: 'e.g. 6 feet' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $4,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 3 weeks' },
      { id: 'gateRequired', label: 'Gate', hint: 'e.g. yes - 1 double gate' },
    ],
  },
  'landscaping': {
    label: 'Landscaping',
    required: ['projectDescription', 'priceMax'],
    fields: [
      { id: 'projectDescription', label: 'Project Description', hint: 'e.g. lawn care, garden design' },
      { id: 'yardSize', label: 'Yard Size', hint: 'e.g. 5,000 sq ft' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $3,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. spring season' },
      { id: 'irrigationSystem', label: 'Irrigation', hint: 'existing, need new, not needed' },
    ],
  },
  'pest-control': {
    label: 'Pest Control',
    required: ['pestType', 'serviceType', 'priceMax'],
    fields: [
      { id: 'pestType', label: 'Pest Type', hint: 'e.g. termites, ants, rodents' },
      { id: 'serviceType', label: 'Service Type', hint: 'one-time, recurring, inspection' },
      { id: 'homeSize', label: 'Home Size', hint: 'e.g. 2,000 sq ft' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $300' },
      { id: 'urgency', label: 'Urgency', hint: 'ASAP, within a week' },
    ],
  },
  'pool-installation': {
    label: 'Pool',
    required: ['projectType', 'priceMax'],
    fields: [
      { id: 'projectType', label: 'Project Type', hint: 'new installation, repair, resurfacing' },
      { id: 'poolType', label: 'Pool Type', hint: 'in-ground, above-ground, infinity' },
      { id: 'poolSize', label: 'Pool Size', hint: 'e.g. 15x30 ft' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $40,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. before summer' },
      { id: 'additionalFeatures', label: 'Features', hint: 'spa, waterfall, lighting' },
    ],
  },
  'solar-panels': {
    label: 'Solar Panels',
    required: ['monthlyElectricBill', 'priceMax'],
    fields: [
      { id: 'monthlyElectricBill', label: 'Monthly Electric Bill (not shared with vendors)', hint: 'e.g. $200/month' },
      { id: 'roofType', label: 'Roof Type', hint: 'asphalt shingle, metal, tile' },
      { id: 'roofAge', label: 'Roof Age', hint: 'e.g. 5 years old' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $20,000' },
      { id: 'batteryStorage', label: 'Battery Storage', hint: 'yes - Tesla Powerwall, no' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 3 months' },
    ],
  },
  'crawl-space': {
    label: 'Crawl Space',
    required: ['serviceType', 'crawlSpaceSize', 'priceMax'],
    fields: [
      { id: 'serviceType', label: 'Service Type', hint: 'encapsulation, vapor barrier, joist repair, dehumidifier, insulation, inspection' },
      { id: 'crawlSpaceSize', label: 'Crawl Space Size (sq ft)', hint: 'e.g. 800 sq ft' },
      { id: 'vaporBarrier', label: 'Vapor Barrier', hint: 'new installation, repair/replacement, not needed' },
      { id: 'joistRepair', label: 'Joist Repair / Sistering', hint: 'yes - damaged/sagging joists, no' },
      { id: 'dehumidifier', label: 'Dehumidifier', hint: 'new installation, replacement, service existing, not needed' },
      { id: 'currentCondition', label: 'Current Condition', hint: 'moisture, mold, pest damage, standing water, general deterioration' },
      { id: 'moistureIssues', label: 'Moisture / Water Issues', hint: 'standing water, high humidity, condensation, none' },
      { id: 'insulationNeeded', label: 'Insulation', hint: 'add new, replace existing, not needed' },
      { id: 'moldRemediation', label: 'Mold Remediation', hint: 'yes - visible mold present, no' },
      { id: 'pestDamage', label: 'Pest / Termite Damage', hint: 'yes - evidence of damage, no' },
      { id: 'ventilationNeeded', label: 'Ventilation / Airflow', hint: 'mechanical ventilation, vent improvements, not needed' },
      { id: 'sumpPump', label: 'Sump Pump', hint: 'new installation, service existing, not needed' },
      { id: 'accessType', label: 'Access Type', hint: 'interior hatch, exterior door, limited access' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $5,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 4 weeks' },
    ],
  },
  'major-appliances': {
    label: 'Appliance',
    required: ['applianceType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'applianceType', label: 'Appliance Type', hint: 'refrigerator, washer/dryer, dishwasher' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Samsung, LG, Whirlpool' },
      { id: 'dimensions', label: 'Dimensions', hint: 'e.g. 30 inch wide, counter-depth' },
      { id: 'finish', label: 'Finish', hint: 'stainless steel, black, white' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,200' },
      { id: 'energyStar', label: 'Energy Star', hint: 'yes, preferred, not required' },
    ],
  },
  'furniture': {
    label: 'Furniture',
    required: ['furnitureType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'furnitureType', label: 'Furniture Type', hint: 'sofa, dining table, bed frame' },
      { id: 'stylePreference', label: 'Style', hint: 'modern, farmhouse, mid-century' },
      { id: 'materialPreference', label: 'Material', hint: 'solid wood, leather, fabric' },
      { id: 'dimensions', label: 'Dimensions', hint: 'e.g. 84 inch sofa' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $800' },
      { id: 'colorFinish', label: 'Color / Finish', hint: 'gray, walnut, white oak' },
    ],
  },
  'mattresses': {
    label: 'Mattress',
    required: ['priceMax', 'mattressSize'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,000' },
      { id: 'mattressSize', label: 'Size', hint: 'twin, full, queen, king' },
      { id: 'mattressType', label: 'Type', hint: 'memory foam, innerspring, hybrid, latex' },
      { id: 'firmness', label: 'Firmness', hint: 'soft, medium, firm' },
      { id: 'height', label: 'Height', hint: 'e.g. 12 inches' },
      { id: 'trialPeriod', label: 'Trial Period', hint: '30, 60, 100, 120, 365 nights' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Tempur-Pedic, Purple' },
    ],
  },
  'home-gym': {
    label: 'Home Gym',
    required: ['equipmentType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'equipmentType', label: 'Equipment Type', hint: 'treadmill, squat rack, dumbbells' },
      { id: 'availableSpace', label: 'Available Space', hint: 'e.g. 10x12 ft room, garage' },
      { id: 'fitnessGoals', label: 'Fitness Goals', hint: 'cardio, strength training, yoga' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $3,000' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Peloton, Bowflex, Rogue' },
    ],
  },
  'generators': {
    label: 'Generator',
    required: ['generatorType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'generatorType', label: 'Generator Type', hint: 'standby, portable, inverter' },
      { id: 'powerOutput', label: 'Power Output', hint: 'e.g. 10kW, 22kW' },
      { id: 'fuelType', label: 'Fuel Type', hint: 'natural gas, propane, gasoline, diesel' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $5,000' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Generac, Kohler' },
    ],
  },
  'home-security': {
    label: 'Home Security',
    required: ['systemType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'systemType', label: 'System Type', hint: 'monitored, self-monitored, cameras only' },
      { id: 'numberOfCameras', label: 'Cameras', hint: 'e.g. 4 cameras' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $800' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Ring, ADT, SimpliSafe' },
      { id: 'smartHomeIntegration', label: 'Smart Home', hint: 'Alexa, Google Home' },
      { id: 'monthlyFee', label: 'Monthly Fee Budget (not shared with vendors)', hint: 'e.g. under $30/month' },
    ],
  },
  'car-purchase': {
    label: 'Car Purchase',
    required: ['vehicleType', 'totalCost'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'vehicleType', label: 'Vehicle Type', hint: 'sedan, SUV, truck, coupe, hatchback' },
      { id: 'fuelType', label: 'Fuel Type', hint: 'gasoline, hybrid, electric, diesel' },
      { id: 'makeModelYear', label: 'Make/Model/Year', hint: 'e.g. 2022 Toyota Camry' },
      { id: 'totalCost', label: 'Total Cost (not shared with vendors)', hint: 'e.g. $28,000 out-the-door' },
      { id: 'mileage', label: 'Mileage (used)', hint: 'e.g. 35,000 miles' },
      { id: 'warranty', label: 'Warranty', hint: 'e.g. 3yr/36k bumper-to-bumper' },
    ],
  },
  'motorcycle': {
    label: 'Motorcycle',
    required: ['motorcycleType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'motorcycleType', label: 'Type', hint: 'cruiser, sport, touring, adventure' },
      { id: 'makeModelYear', label: 'Make/Model/Year', hint: 'e.g. 2023 Harley-Davidson' },
      { id: 'engineSize', label: 'Engine Size', hint: 'e.g. 1200cc, 650cc' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $12,000' },
    ],
  },
  'rv': {
    label: 'RV',
    required: ['rvType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'rvType', label: 'RV Type', hint: 'Class A, Class B, travel trailer, fifth wheel' },
      { id: 'length', label: 'Length', hint: 'e.g. 30 ft, 40 ft' },
      { id: 'sleepingCapacity', label: 'Sleeping Capacity', hint: 'e.g. sleeps 4' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $60,000' },
    ],
  },
  'boat': {
    label: 'Boat',
    required: ['boatType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'boatType', label: 'Boat Type', hint: 'pontoon, fishing, ski/wake, sailboat' },
      { id: 'length', label: 'Length', hint: 'e.g. 22 ft' },
      { id: 'engineType', label: 'Engine Type', hint: 'outboard, inboard, sterndrive' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $35,000' },
    ],
  },
  'car-insurance': {
    label: 'Car Insurance',
    required: ['vehicleInfo', 'driverAge', 'coverageType'],
    fields: [
      { id: 'vehicleInfo', label: 'Vehicle', hint: 'e.g. 2020 Toyota Camry' },
      { id: 'driverAge', label: 'Driver Age', hint: 'e.g. 32' },
      { id: 'coverageType', label: 'Coverage Type', hint: 'liability only, full coverage, comprehensive' },
      { id: 'currentPremium', label: 'Current Premium (not shared with vendors)', hint: 'e.g. $150/month' },
      { id: 'drivingRecord', label: 'Driving Record', hint: 'clean, 1 minor violation' },
      { id: 'annualMileage', label: 'Annual Mileage', hint: 'e.g. 12,000 miles/year' },
    ],
  },
  'auto-repair': {
    label: 'Auto Repair',
    required: ['vehicleInfo', 'issueDescription', 'priceMax'],
    fields: [
      { id: 'vehicleInfo', label: 'Vehicle', hint: 'e.g. 2018 Honda Civic' },
      { id: 'issueDescription', label: 'Issue', hint: 'e.g. check engine light, brake noise' },
      { id: 'mileage', label: 'Mileage', hint: 'e.g. 85,000 miles' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
    ],
  },
  'tires': {
    label: 'Tires',
    required: ['vehicleInfo', 'tireSize', 'tireType', 'numberOfTires', 'priceMax'],
    fields: [
      { id: 'vehicleInfo', label: 'Vehicle', hint: 'e.g. 2019 Ford F-150' },
      { id: 'tireSize', label: 'Tire Size', hint: 'e.g. 225/65R17' },
      { id: 'tireType', label: 'Tire Type', hint: 'all-season, summer, winter, all-terrain' },
      { id: 'numberOfTires', label: 'Number of Tires', hint: 'e.g. 4 tires' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $600' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. Michelin, Goodyear' },
    ],
  },
  'detailing': {
    label: 'Auto Detailing',
    required: ['vehicleInfo', 'serviceLevel', 'priceMax'],
    fields: [
      { id: 'vehicleInfo', label: 'Vehicle', hint: 'e.g. 2021 BMW 3 Series' },
      { id: 'serviceLevel', label: 'Service Level', hint: 'basic wash, full detail, paint correction, ceramic coating' },
      { id: 'vehicleCondition', label: 'Condition', hint: 'light dirt, heavily soiled, pet hair' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $300' },
      { id: 'mobileOrShop', label: 'Mobile or Shop', hint: 'mobile preferred, shop ok' },
    ],
  },
  'body-shop': {
    label: 'Body Shop',
    required: ['vehicleInfo', 'damageDescription', 'priceMax'],
    fields: [
      { id: 'vehicleInfo', label: 'Vehicle', hint: 'e.g. 2020 Chevy Silverado' },
      { id: 'damageDescription', label: 'Damage', hint: 'e.g. rear bumper dent, door scratch' },
      { id: 'insuranceClaim', label: 'Insurance Claim', hint: 'yes - State Farm, paying out of pocket' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $2,000' },
    ],
  },
  'weddings': {
    label: 'Wedding',
    required: ['weddingDate', 'guestCount', 'location', 'totalBudget'],
    fields: [
      { id: 'weddingDate', label: 'Wedding Date', hint: 'e.g. June 15, 2025' },
      { id: 'guestCount', label: 'Guest Count', hint: 'e.g. 150 guests' },
      { id: 'venueType', label: 'Venue Type', hint: 'outdoor, ballroom, barn' },
      { id: 'location', label: 'Location', hint: 'e.g. Austin, TX' },
      { id: 'totalBudget', label: 'Total Budget (not shared with vendors)', hint: 'e.g. $30,000' },
      { id: 'servicesNeeded', label: 'Services Needed', hint: 'catering, photography, DJ, flowers' },
    ],
  },
  'engagement-rings': {
    label: 'Engagement Ring',
    required: ['metalType', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'metalType', label: 'Metal Type', hint: 'white gold, yellow gold, rose gold, platinum' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, lab diamond, sapphire, moissanite' },
      { id: 'caratSize', label: 'Carat Size', hint: 'e.g. 1.5 carat' },
      { id: 'ringStyle', label: 'Ring Style', hint: 'solitaire, halo, vintage' },
      { id: 'ringSize', label: 'Ring Size', hint: 'e.g. size 6' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $5,000' },
    ],
  },
  'birthdays': {
    label: 'Birthday / Celebration',
    required: ['eventType', 'guestCount', 'eventDate', 'totalBudget'],
    fields: [
      { id: 'eventType', label: 'Event Type', hint: 'birthday party, milestone celebration' },
      { id: 'guestCount', label: 'Guest Count', hint: 'e.g. 50 guests' },
      { id: 'eventDate', label: 'Event Date', hint: 'e.g. March 20, 2025' },
      { id: 'venuePreference', label: 'Venue', hint: 'restaurant, home, event hall' },
      { id: 'totalBudget', label: 'Total Budget (not shared with vendors)', hint: 'e.g. $5,000' },
      { id: 'servicesNeeded', label: 'Services Needed', hint: 'catering, DJ, decorations, cake' },
    ],
  },
  'funerals': {
    label: 'Funeral / Memorial',
    required: ['serviceType', 'location', 'totalBudget', 'timeline'],
    fields: [
      { id: 'serviceType', label: 'Service Type', hint: 'burial, cremation, celebration of life, graveside' },
      { id: 'expectedAttendees', label: 'Expected Attendees', hint: 'e.g. 75 people' },
      { id: 'location', label: 'Location', hint: 'e.g. Dallas, TX' },
      { id: 'totalBudget', label: 'Total Budget (not shared with vendors)', hint: 'e.g. $10,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 5 days' },
      { id: 'specialRequests', label: 'Special Requests', hint: 'military honors, live music' },
    ],
  },
  'childcare': {
    label: 'Childcare',
    required: ['childAge', 'careType', 'priceMax'],
    fields: [
      { id: 'childAge', label: 'Child Age(s)', hint: 'e.g. 2 years, 4 years' },
      { id: 'careType', label: 'Care Type', hint: 'daycare center, home daycare, nanny, au pair' },
      { id: 'hoursNeeded', label: 'Hours Needed', hint: 'e.g. 12 hours/week' },
      { id: 'priceMax', label: 'Max Monthly Budget (not shared with vendors)', hint: 'e.g. $1,500/month' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. near downtown Austin' },
    ],
  },
  'baby-products': {
    label: 'Baby Product',
    required: ['babyProductType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'babyProductType', label: 'Product Type', hint: 'stroller, crib, car seat, bassinet, high chair, baby monitor, baby carrier' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. UPPAbaby, Graco, Chicco' },
      { id: 'keyFeatures', label: 'Key Features', hint: 'e.g. foldable, car seat compatible' },
      { id: 'childAgeWeight', label: 'Child Age/Weight', hint: 'e.g. newborn, up to 50 lbs' },
    ],
  },
  'private-school': {
    label: 'Private School',
    required: ['gradeLevel', 'annualTuition'],
    fields: [
      { id: 'gradeLevel', label: 'Grade Level', hint: 'K-5, middle school, high school' },
      { id: 'annualTuition', label: 'Annual Tuition Budget (not shared with vendors)', hint: 'e.g. $15,000/year' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. within 10 miles of zip 78701' },
      { id: 'schoolType', label: 'School Type', hint: 'Montessori, religious, college-prep' },
      { id: 'specialPrograms', label: 'Special Programs', hint: 'STEM, arts, sports' },
    ],
  },
  'flights': {
    label: 'Flight',
    required: ['origin', 'destination', 'travelDates', 'passengers', 'priceMax'],
    fields: [
      { id: 'origin', label: 'Origin', hint: 'e.g. Austin, TX (AUS)' },
      { id: 'destination', label: 'Destination', hint: 'e.g. New York, NY (JFK)' },
      { id: 'travelDates', label: 'Travel Dates', hint: 'e.g. March 15 - March 22' },
      { id: 'passengers', label: 'Passengers', hint: 'e.g. 2 adults, 1 child' },
      { id: 'cabinClass', label: 'Cabin Class', hint: 'economy, premium economy, business, first' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $200 per person' },
    ],
  },
  'hotels': {
    label: 'Hotel',
    required: ['destination', 'checkInOut', 'numberOfGuests', 'priceMax'],
    fields: [
      { id: 'destination', label: 'Destination', hint: 'e.g. Miami Beach, FL' },
      { id: 'checkInOut', label: 'Check-in / Check-out', hint: 'e.g. April 10 - April 15' },
      { id: 'numberOfGuests', label: 'Guests', hint: 'e.g. 2 adults' },
      { id: 'starRating', label: 'Star Rating', hint: '3-star, 4-star, 5-star, boutique' },
      { id: 'priceMax', label: 'Max Budget per Night (not shared with vendors)', hint: 'e.g. $200/night' },
      { id: 'amenitiesRequired', label: 'Amenities', hint: 'pool, gym, free parking' },
    ],
  },
  'vacation-packages': {
    label: 'Vacation Package',
    required: ['destination', 'travelDates', 'numberOfTravelers', 'priceMax'],
    fields: [
      { id: 'destination', label: 'Destination', hint: 'e.g. Cancun, Mexico' },
      { id: 'travelDates', label: 'Travel Dates', hint: 'e.g. July 4 - July 11' },
      { id: 'numberOfTravelers', label: 'Number of Travelers', hint: 'e.g. 2 adults, 2 children' },
      { id: 'priceMax', label: 'Total Budget (not shared with vendors)', hint: 'e.g. $5,000' },
      { id: 'packageInclusions', label: 'Package Inclusions', hint: 'flights, hotel, meals, excursions' },
      { id: 'travelStyle', label: 'Travel Style', hint: 'all-inclusive, adventure, relaxation' },
    ],
  },
  'cruises': {
    label: 'Cruise',
    required: ['departurePort', 'destination', 'travelDates', 'numberOfGuests', 'priceMax'],
    fields: [
      { id: 'departurePort', label: 'Departure Port', hint: 'e.g. Miami, FL' },
      { id: 'destination', label: 'Destination', hint: 'Caribbean, Mediterranean' },
      { id: 'travelDates', label: 'Travel Dates', hint: 'e.g. November 2025' },
      { id: 'duration', label: 'Duration', hint: 'e.g. 7 nights' },
      { id: 'numberOfGuests', label: 'Guests', hint: 'e.g. 2 adults' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,500 per person' },
      { id: 'cruiseLinePreference', label: 'Cruise Line', hint: 'e.g. Royal Caribbean, Carnival' },
    ],
  },
  'rental-cars': {
    label: 'Rental Car',
    required: ['pickupLocation', 'rentalDates', 'vehicleClass', 'priceMax'],
    fields: [
      { id: 'pickupLocation', label: 'Pickup Location', hint: 'e.g. Austin-Bergstrom Airport' },
      { id: 'rentalDates', label: 'Rental Dates', hint: 'e.g. May 1 - May 7' },
      { id: 'vehicleClass', label: 'Vehicle Class', hint: 'economy, compact, midsize, SUV, luxury' },
      { id: 'priceMax', label: 'Max Daily Rate (not shared with vendors)', hint: 'e.g. $60/day' },
      { id: 'driverAge', label: 'Driver Age', hint: 'e.g. 28' },
    ],
  },
  'travel-insurance': {
    label: 'Travel Insurance',
    required: ['tripDestination', 'tripDates', 'numberOfTravelers', 'tripCost', 'priceMax'],
    fields: [
      { id: 'tripDestination', label: 'Destination', hint: 'e.g. Europe, Japan' },
      { id: 'tripDates', label: 'Trip Dates', hint: 'e.g. Sept 1 - Sept 15' },
      { id: 'numberOfTravelers', label: 'Travelers', hint: 'e.g. 2 adults' },
      { id: 'tripCost', label: 'Trip Cost (not shared with vendors)', hint: 'e.g. $6,000 total' },
      { id: 'coverageNeeded', label: 'Coverage Needed', hint: 'trip cancellation, medical, baggage' },
      { id: 'priceMax', label: 'Max Premium (not shared with vendors)', hint: 'e.g. $300' },
    ],
  },
  'concerts': {
    label: 'Concert Tickets',
    required: ['artistEvent', 'eventDate', 'numberOfTickets', 'priceMax'],
    fields: [
      { id: 'artistEvent', label: 'Artist / Event', hint: 'e.g. Taylor Swift, Beyonce' },
      { id: 'eventDate', label: 'Event Date', hint: 'e.g. August 10, 2025' },
      { id: 'venue', label: 'Venue', hint: 'e.g. Madison Square Garden' },
      { id: 'numberOfTickets', label: 'Number of Tickets', hint: 'e.g. 2 tickets' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $200 per ticket' },
      { id: 'seatingPreference', label: 'Seating', hint: 'floor, lower bowl, VIP' },
    ],
  },
  'sporting-events': {
    label: 'Sporting Event Tickets',
    required: ['sportEvent', 'eventDate', 'numberOfTickets', 'priceMax'],
    fields: [
      { id: 'sportEvent', label: 'Sport / Team / Event', hint: 'e.g. NFL Cowboys vs Eagles' },
      { id: 'eventDate', label: 'Event Date', hint: 'e.g. October 5, 2025' },
      { id: 'venue', label: 'Venue', hint: 'e.g. AT&T Stadium' },
      { id: 'numberOfTickets', label: 'Number of Tickets', hint: 'e.g. 4 tickets' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $150 per ticket' },
      { id: 'seatingPreference', label: 'Seating', hint: 'lower level, club seats' },
    ],
  },
  'courses': {
    label: 'Course',
    required: ['courseTopic', 'format', 'priceMax'],
    fields: [
      { id: 'courseTopic', label: 'Course Topic', hint: 'e.g. Python programming, digital marketing' },
      { id: 'format', label: 'Format', hint: 'online self-paced, online live, in-person, hybrid' },
      { id: 'duration', label: 'Duration', hint: 'e.g. 8 weeks, 3 months' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
      { id: 'examPrepIncluded', label: 'Exam Prep', hint: 'yes, preferred' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. 3 months' },
    ],
  },
  'gym-memberships': {
    label: 'Gym Membership',
    required: ['location', 'monthlyBudget'],
    fields: [
      { id: 'location', label: 'Location', hint: 'e.g. near downtown Austin' },
      { id: 'monthlyBudget', label: 'Max Monthly Budget (not shared with vendors)', hint: 'e.g. $60/month' },
      { id: 'gymType', label: 'Gym Type', hint: '24-hour, boutique, CrossFit, yoga' },
      { id: 'requiredAmenities', label: 'Amenities', hint: 'pool, sauna, group classes' },
      { id: 'contractPreference', label: 'Contract', hint: 'no contract, month-to-month' },
    ],
  },
  'credit-cards': {
    label: 'Credit Card',
    required: ['cardType', 'creditScore'],
    fields: [
      { id: 'cardType', label: 'Card Type', hint: 'rewards, cash back, travel, balance transfer, secured' },
      { id: 'creditScore', label: 'Credit Score (not shared with vendors)', hint: 'e.g. 700-720' },
      { id: 'annualFeeTolerance', label: 'Annual Fee Tolerance (not shared with vendors)', hint: 'no annual fee, up to $95' },
      { id: 'monthlySpend', label: 'Monthly Spend (not shared with vendors)', hint: 'e.g. $2,000/month' },
      { id: 'spendingCategories', label: 'Spending Categories', hint: 'dining, travel, groceries' },
      { id: 'signOnBonus', label: 'Sign-on Bonus', hint: 'yes, preferred' },
    ],
  },
  'accountants': {
    label: 'Accountant',
    required: ['serviceType', 'businessOrPersonal', 'priceMax'],
    fields: [
      { id: 'serviceType', label: 'Service Type', hint: 'tax prep, bookkeeping, audit, business accounting' },
      { id: 'businessOrPersonal', label: 'Business or Personal', hint: 'personal, small business' },
      { id: 'annualRevenue', label: 'Annual Revenue / Income (not shared with vendors)', hint: 'e.g. $150,000' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
      { id: 'locationPreference', label: 'Location', hint: 'local CPA, remote ok' },
    ],
  },
  'attorneys': {
    label: 'Attorney',
    required: ['practiceArea', 'caseDescription', 'priceMax'],
    fields: [
      { id: 'practiceArea', label: 'Practice Area', hint: 'family law, real estate, business, estate planning, personal injury' },
      { id: 'caseDescription', label: 'Case Description', hint: 'e.g. divorce proceedings, contract review' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $3,000' },
      { id: 'location', label: 'Location', hint: 'e.g. Austin, TX' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. need consultation within 2 weeks' },
    ],
  },
  'financial-advisors': {
    label: 'Financial Advisor',
    required: ['serviceType', 'financialGoals'],
    fields: [
      { id: 'serviceType', label: 'Service Type', hint: 'retirement planning, investment management, tax planning, estate planning' },
      { id: 'investableAssets', label: 'Investable Assets (not shared with vendors)', hint: 'e.g. $250,000' },
      { id: 'annualIncome', label: 'Annual Income (not shared with vendors)', hint: 'e.g. $120,000' },
      { id: 'feePreference', label: 'Fee Preference', hint: 'fee-only, AUM-based' },
      { id: 'financialGoals', label: 'Financial Goals', hint: 'e.g. retire at 55, college fund' },
    ],
  },
  'moving-companies': {
    label: 'Moving Company',
    required: ['moveFrom', 'moveTo', 'moveDate', 'homeSize', 'priceMax'],
    fields: [
      { id: 'moveFrom', label: 'Move From', hint: 'e.g. Austin, TX' },
      { id: 'moveTo', label: 'Move To', hint: 'e.g. Dallas, TX' },
      { id: 'moveDate', label: 'Move Date', hint: 'e.g. April 15, 2025' },
      { id: 'homeSize', label: 'Home Size', hint: 'e.g. 2-bedroom apartment' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $2,000' },
      { id: 'packingServices', label: 'Packing Services', hint: 'full packing, partial, self-pack' },
    ],
  },
  'tutors': {
    label: 'Tutor',
    required: ['subject', 'gradeLevel', 'format', 'priceMax'],
    fields: [
      { id: 'subject', label: 'Subject', hint: 'e.g. Algebra, SAT prep, Spanish' },
      { id: 'gradeLevel', label: 'Grade Level', hint: 'e.g. 9th grade, college freshman' },
      { id: 'format', label: 'Format', hint: 'in-person, online, either' },
      { id: 'sessionsPerWeek', label: 'Sessions per Week', hint: 'e.g. 3 sessions/week' },
      { id: 'priceMax', label: 'Max Hourly Rate (not shared with vendors)', hint: 'e.g. $50/hour' },
      { id: 'experienceLevel', label: 'Experience Level', hint: 'tutor, mentor, experienced' },
    ],
  },
  'auto-insurance': {
    label: 'Auto Insurance',
    required: ['vehicleInfo', 'driverAge', 'coverageType'],
    fields: [
      { id: 'vehicleInfo', label: 'Vehicle', hint: 'e.g. 2020 Toyota Camry' },
      { id: 'driverAge', label: 'Driver Age', hint: 'e.g. 32' },
      { id: 'coverageType', label: 'Coverage Type', hint: 'liability only, full coverage, comprehensive' },
      { id: 'currentPremium', label: 'Current Premium (not shared with vendors)', hint: 'e.g. $150/month' },
      { id: 'drivingRecord', label: 'Driving Record', hint: 'clean, 1 minor violation' },
      { id: 'desiredDeductible', label: 'Deductible', hint: 'e.g. $500, $1,000' },
    ],
  },
  'homeowners-insurance': {
    label: 'Homeowners Insurance',
    required: ['homeValue', 'coverageAmount'],
    fields: [
      { id: 'homeValue', label: 'Home Value (not shared with vendors)', hint: 'e.g. $400,000' },
      { id: 'homeAge', label: 'Home Age', hint: 'e.g. built in 2005' },
      { id: 'squareFootage', label: 'Square Footage', hint: 'e.g. 2,200 sq ft' },
      { id: 'currentPremium', label: 'Current Premium (not shared with vendors)', hint: 'e.g. $1,800/year' },
      { id: 'coverageAmount', label: 'Coverage Amount (not shared with vendors)', hint: 'e.g. $400,000 dwelling' },
      { id: 'desiredDeductible', label: 'Deductible', hint: 'e.g. $1,000, $2,500' },
    ],
  },
  'renters-insurance': {
    label: 'Renters Insurance',
    required: ['rentalAddress', 'personalPropertyValue', 'priceMax'],
    fields: [
      { id: 'rentalAddress', label: 'Rental City / Zip', hint: 'e.g. Austin, TX 78701' },
      { id: 'personalPropertyValue', label: 'Property Value (not shared with vendors)', hint: 'e.g. $20,000' },
      { id: 'priceMax', label: 'Max Monthly Premium (not shared with vendors)', hint: 'e.g. $20/month' },
      { id: 'liabilityCoverage', label: 'Liability Coverage', hint: 'e.g. $100,000' },
      { id: 'roommates', label: 'Roommates', hint: 'e.g. 1 roommate to add' },
    ],
  },
  'life-insurance': {
    label: 'Life Insurance',
    required: ['policyType', 'coverageAmount', 'applicantAge', 'priceMax'],
    fields: [
      { id: 'policyType', label: 'Policy Type', hint: 'term, whole, universal, variable' },
      { id: 'coverageAmount', label: 'Coverage Amount (not shared with vendors)', hint: 'e.g. $500,000' },
      { id: 'termLength', label: 'Term Length', hint: 'e.g. 20 years, 30 years' },
      { id: 'applicantAge', label: 'Applicant Age', hint: 'e.g. 35' },
      { id: 'healthStatus', label: 'Health Status', hint: 'non-smoker, excellent health' },
      { id: 'priceMax', label: 'Max Monthly Premium (not shared with vendors)', hint: 'e.g. $50/month' },
    ],
  },
  'health-insurance': {
    label: 'Health Insurance',
    required: ['planType', 'numberOfPeople', 'priceMax'],
    fields: [
      { id: 'planType', label: 'Plan Type', hint: 'HMO, PPO, EPO, HDHP+HSA' },
      { id: 'numberOfPeople', label: 'People to Cover', hint: 'individual, family of 4' },
      { id: 'priceMax', label: 'Max Monthly Premium (not shared with vendors)', hint: 'e.g. $400/month' },
      { id: 'desiredDeductible', label: 'Deductible', hint: 'e.g. $1,500, $3,000' },
      { id: 'preferredNetwork', label: 'Preferred Network', hint: 'e.g. Blue Cross, keep current PCP' },
      { id: 'preExistingConditions', label: 'Pre-existing Conditions', hint: 'e.g. diabetes, asthma' },
    ],
  },
  'pet-insurance': {
    label: 'Pet Insurance',
    required: ['petInfo', 'coverageType', 'priceMax'],
    fields: [
      { id: 'petInfo', label: 'Pet Type / Breed', hint: 'e.g. Golden Retriever, 3 years old' },
      { id: 'coverageType', label: 'Coverage Type', hint: 'accident only, accident & illness, comprehensive' },
      { id: 'priceMax', label: 'Max Monthly Premium (not shared with vendors)', hint: 'e.g. $50/month' },
      { id: 'annualDeductible', label: 'Annual Deductible', hint: 'e.g. $250, $500' },
      { id: 'reimbursementRate', label: 'Reimbursement Rate', hint: 'e.g. 80%, 90%' },
    ],
  },
  'business-insurance': {
    label: 'Business Insurance',
    required: ['businessType', 'coverageType', 'priceMax'],
    fields: [
      { id: 'businessType', label: 'Business Type', hint: 'LLC, sole proprietor, restaurant' },
      { id: 'coverageType', label: 'Coverage Type', hint: 'general liability, professional liability, BOP, workers comp, cyber' },
      { id: 'annualRevenue', label: 'Annual Revenue (not shared with vendors)', hint: 'e.g. $500,000' },
      { id: 'numberOfEmployees', label: 'Employees', hint: 'e.g. 5 employees' },
      { id: 'priceMax', label: 'Max Annual Premium (not shared with vendors)', hint: 'e.g. $2,000/year' },
    ],
  },
  'college': {
    label: 'College / University',
    required: ['degreeType', 'fieldOfStudy', 'annualTuition', 'attendanceFormat'],
    fields: [
      { id: 'degreeType', label: 'Degree Type', hint: "Bachelor's, Associate's, Master's" },
      { id: 'fieldOfStudy', label: 'Field of Study', hint: 'e.g. Computer Science, Business' },
      { id: 'annualTuition', label: 'Annual Tuition Budget (not shared with vendors)', hint: 'e.g. $25,000/year' },
      { id: 'attendanceFormat', label: 'Format', hint: 'on-campus, online, hybrid' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. Texas, Southeast US, any' },
      { id: 'startDate', label: 'Start Date', hint: 'e.g. Fall 2025' },
    ],
  },
  'student-loans': {
    label: 'Student Loan',
    required: ['loanType', 'loanAmount', 'schoolProgram'],
    fields: [
      { id: 'loanType', label: 'Loan Type', hint: 'federal, private, refinance' },
      { id: 'loanAmount', label: 'Loan Amount (not shared with vendors)', hint: 'e.g. $30,000/year' },
      { id: 'schoolProgram', label: 'School / Program', hint: 'e.g. UT Austin, MBA program' },
      { id: 'creditScore', label: 'Credit Score (not shared with vendors)', hint: 'e.g. 680' },
      { id: 'coSignerAvailable', label: 'Co-signer', hint: 'yes, no' },
      { id: 'repaymentPreference', label: 'Repayment', hint: 'income-driven, fixed 10-year' },
    ],
  },
  'bootcamps': {
    label: 'Bootcamp',
    required: ['bootcampFocus', 'format', 'priceMax'],
    fields: [
      { id: 'bootcampFocus', label: 'Bootcamp Focus', hint: 'software engineering, UX design, data science' },
      { id: 'format', label: 'Format', hint: 'full-time, part-time, online, in-person' },
      { id: 'duration', label: 'Duration', hint: 'e.g. 12 weeks, 6 months' },
      { id: 'priceMax', label: 'Max Tuition (not shared with vendors)', hint: 'e.g. $15,000' },
      { id: 'jobPlacementGuarantee', label: 'Job Placement', hint: 'required, preferred' },
      { id: 'startDate', label: 'Start Date', hint: 'next cohort, flexible' },
    ],
  },
  'certifications': {
    label: 'Certification',
    required: ['certificationName', 'format', 'priceMax'],
    fields: [
      { id: 'certificationName', label: 'Certification Name', hint: 'e.g. AWS Solutions Architect, PMP, CPA' },
      { id: 'format', label: 'Format', hint: 'online self-paced, online live, in-person' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $800' },
      { id: 'examPrepIncluded', label: 'Exam Prep', hint: 'yes, preferred' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. 3 months' },
    ],
  },
  'online-learning': {
    label: 'Online Learning',
    required: ['topic', 'priceMax'],
    fields: [
      { id: 'topic', label: 'Topic / Subject', hint: 'e.g. Python, photography, finance' },
      { id: 'platformPreference', label: 'Platform', hint: 'Coursera, Udemy, LinkedIn Learning' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $100' },
      { id: 'skillLevel', label: 'Skill Level', hint: 'beginner, intermediate' },
      { id: 'certificateNeeded', label: 'Certificate', hint: 'yes, no' },
    ],
  },
  'professional-training': {
    label: 'Professional Training',
    required: ['trainingTopic', 'format', 'priceMax'],
    fields: [
      { id: 'trainingTopic', label: 'Training Topic', hint: 'leadership, sales, project management' },
      { id: 'format', label: 'Format', hint: 'in-person workshop, online live, on-demand' },
      { id: 'numberOfAttendees', label: 'Attendees', hint: 'e.g. 1 person, team of 10' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,000' },
      { id: 'duration', label: 'Duration', hint: 'e.g. 2-day workshop, 4 weeks' },
    ],
  },
  'tutoring': {
    label: 'Tutoring',
    required: ['subject', 'gradeLevel', 'format', 'priceMax'],
    fields: [
      { id: 'subject', label: 'Subject', hint: 'e.g. Math, Reading, SAT prep' },
      { id: 'gradeLevel', label: 'Grade Level', hint: 'e.g. 5th grade, high school' },
      { id: 'format', label: 'Format', hint: 'in-person, online, either' },
      { id: 'sessionsPerWeek', label: 'Sessions per Week', hint: 'e.g. 2 sessions/week' },
      { id: 'priceMax', label: 'Max Hourly Rate (not shared with vendors)', hint: 'e.g. $40/hour' },
      { id: 'specificGoals', label: 'Goals', hint: 'improve grade, pass exam' },
    ],
  },
  'dental-care': {
    label: 'Dental Care',
    required: ['serviceNeeded', 'priceMax'],
    fields: [
      { id: 'serviceNeeded', label: 'Service Needed', hint: 'e.g. cleaning, braces, implants, whitening' },
      { id: 'numberOfPatients', label: 'Patients', hint: 'e.g. 1 adult, family of 4' },
      { id: 'insurance', label: 'Insurance', hint: 'e.g. Delta Dental, no insurance' },
      { id: 'priceMax', label: 'Max Out-of-Pocket (not shared with vendors)', hint: 'e.g. $500' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. near downtown' },
    ],
  },
  'vision-care': {
    label: 'Vision Care',
    required: ['serviceNeeded', 'priceMax'],
    fields: [
      { id: 'serviceNeeded', label: 'Service Needed', hint: 'e.g. eye exam, glasses, contacts, LASIK' },
      { id: 'insurance', label: 'Insurance', hint: 'e.g. VSP, EyeMed, no insurance' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $300' },
      { id: 'frameStyle', label: 'Frame Style', hint: 'modern, classic, rimless' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. near downtown' },
    ],
  },
  'mental-health': {
    label: 'Mental Health',
    required: ['serviceType', 'format', 'priceMax'],
    fields: [
      { id: 'serviceType', label: 'Service Type', hint: 'individual therapy, couples therapy, psychiatry, coaching' },
      { id: 'format', label: 'Format', hint: 'in-person, telehealth, either' },
      { id: 'insurance', label: 'Insurance', hint: 'e.g. Blue Cross, self-pay' },
      { id: 'priceMax', label: 'Max Session Cost (not shared with vendors)', hint: 'e.g. $150/session' },
      { id: 'specialtyNeeded', label: 'Specialty', hint: 'anxiety, depression, trauma, ADHD' },
      { id: 'availability', label: 'Availability', hint: 'evenings, weekends, near 78701' },
    ],
  },
  'physical-therapy': {
    label: 'Physical Therapy',
    required: ['conditionInjury', 'priceMax'],
    fields: [
      { id: 'conditionInjury', label: 'Condition / Injury', hint: 'e.g. lower back pain, neck stiffness' },
      { id: 'numberOfSessions', label: 'Sessions', hint: 'e.g. 8 sessions' },
      { id: 'insurance', label: 'Insurance', hint: 'e.g. Aetna, self-pay' },
      { id: 'priceMax', label: 'Max Session Cost (not shared with vendors)', hint: 'e.g. $80/session' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. near 78701' },
    ],
  },
  'gym-fitness': {
    label: 'Gym / Fitness',
    required: ['location', 'monthlyBudget'],
    fields: [
      { id: 'location', label: 'Location', hint: 'e.g. near downtown Austin' },
      { id: 'monthlyBudget', label: 'Max Monthly Budget (not shared with vendors)', hint: 'e.g. $60/month' },
      { id: 'gymType', label: 'Gym Type', hint: '24-hour, boutique, CrossFit, yoga' },
      { id: 'requiredAmenities', label: 'Amenities', hint: 'pool, sauna, group classes' },
      { id: 'contractPreference', label: 'Contract', hint: 'no contract, month-to-month' },
    ],
  },
  'personal-training': {
    label: 'Personal Training',
    required: ['fitnessGoals', 'trainingLocation', 'priceMax'],
    fields: [
      { id: 'fitnessGoals', label: 'Fitness Goals', hint: 'weight loss, muscle gain, marathon prep' },
      { id: 'trainingLocation', label: 'Training Location', hint: 'gym, at home, outdoor, online' },
      { id: 'sessionsPerWeek', label: 'Sessions per Week', hint: 'e.g. 3 sessions/week' },
      { id: 'priceMax', label: 'Max Hourly Rate (not shared with vendors)', hint: 'e.g. $80/session' },
      { id: 'experienceLevel', label: 'Experience Level', hint: 'beginner, intermediate' },
    ],
  },
  'nutrition-dietitian': {
    label: 'Nutrition / Dietitian',
    required: ['healthGoals', 'serviceFormat', 'priceMax'],
    fields: [
      { id: 'healthGoals', label: 'Health Goals', hint: 'weight loss, muscle gain, manage diabetes' },
      { id: 'serviceFormat', label: 'Format', hint: 'in-person, telehealth, either' },
      { id: 'numberOfSessions', label: 'Sessions', hint: 'e.g. 6 sessions' },
      { id: 'priceMax', label: 'Max Session Cost (not shared with vendors)', hint: 'e.g. $120/session' },
      { id: 'dietaryRestrictions', label: 'Dietary Restrictions', hint: 'e.g. vegan, gluten-free, diabetic' },
    ],
  },
  'chiropractic': {
    label: 'Chiropractic',
    required: ['conditionComplaint', 'priceMax'],
    fields: [
      { id: 'conditionComplaint', label: 'Condition / Complaint', hint: 'e.g. lower back pain, neck stiffness' },
      { id: 'numberOfSessions', label: 'Sessions', hint: 'e.g. 8 sessions' },
      { id: 'insurance', label: 'Insurance', hint: 'e.g. United Healthcare, self-pay' },
      { id: 'priceMax', label: 'Max Session Cost (not shared with vendors)', hint: 'e.g. $80/session' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. near 78701' },
    ],
  },
  'medical-equipment': {
    label: 'Medical Equipment',
    required: ['equipmentType', 'purchaseOrRent', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'equipmentType', label: 'Equipment Type', hint: 'CPAP, wheelchair, blood pressure monitor' },
      { id: 'purchaseOrRent', label: 'Purchase or Rent', hint: 'purchase, rent/lease' },
      { id: 'insuranceCoverage', label: 'Insurance', hint: 'Medicare, Aetna, self-pay' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
      { id: 'brandPreference', label: 'Brand', hint: 'e.g. ResMed, Philips' },
    ],
  },
  'cosmetic-procedures': {
    label: 'Cosmetic Procedure',
    required: ['procedureType', 'providerType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'procedureType', label: 'Procedure Type', hint: 'Botox, lip filler, rhinoplasty' },
      { id: 'providerType', label: 'Provider Type', hint: 'plastic surgeon, dermatologist, med spa' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,500' },
      { id: 'locationPreference', label: 'Location', hint: 'e.g. Austin, TX' },
      { id: 'boardCertified', label: 'Board Certified', hint: 'yes, required' },
    ],
  },
  'weight-loss-programs': {
    label: 'Weight Loss Program',
    required: ['programType', 'weightLossGoal', 'monthlyBudget'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'programType', label: 'Program Type', hint: 'medical, diet program, bariatric surgery, coaching' },
      { id: 'weightLossGoal', label: 'Weight Loss Goal', hint: 'e.g. lose 30 lbs in 6 months' },
      { id: 'monthlyBudget', label: 'Max Monthly Budget (not shared with vendors)', hint: 'e.g. $200/month' },
      { id: 'insuranceCoverage', label: 'Insurance Coverage', hint: 'yes, no' },
      { id: 'previousAttempts', label: 'Previous Attempts', hint: 'tried keto, gym, medication' },
    ],
  },
  'sunglasses': {
    label: 'Sunglasses',
    required: ['frameStyle', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Ray-Ban, Oakley, Maui Jim' },
      { id: 'frameStyle', label: 'Frame Style', hint: 'aviator, wayfarer, round, sport, oversized' },
      { id: 'lensType', label: 'Lens Type', hint: 'polarized, mirrored, photochromic, gradient' },
      { id: 'uvProtection', label: 'UV Protection', hint: 'e.g. UV400, 100% UV protection' },
      { id: 'material', label: 'Frame Material', hint: 'acetate, metal, titanium, plastic' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $200' },
    ],
  },
  'watches': {
    label: 'Watch',
    required: ['style', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'style', label: 'Style', hint: 'dress, sport, casual, luxury, smartwatch, dive, pilot, field' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Rolex, Seiko, Apple Watch, Casio, Omega' },
      { id: 'movementType', label: 'Movement Type', hint: 'automatic, quartz, mechanical, solar, kinetic' },
      { id: 'caseSize', label: 'Case Size', hint: 'e.g. 38mm, 40mm, 42mm, 44mm' },
      { id: 'bandType', label: 'Band Type', hint: 'leather, rubber, metal bracelet, NATO, silicone, mesh' },
      { id: 'caseMaterial', label: 'Case Material', hint: 'stainless steel, titanium, gold, ceramic, plastic' },
      { id: 'waterResistance', label: 'Water Resistance', hint: 'e.g. 30m, 100m, 200m, none' },
      { id: 'color', label: 'Color', hint: 'e.g. black, silver, gold, rose gold, blue' },
      { id: 'priceMax', label: 'Price (not shared with vendors)', hint: 'e.g. $500' },
    ],
  },
  'belts': {
    label: 'Belt',
    required: ['material', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'material', label: 'Material', hint: 'leather, canvas, suede, synthetic' },
      { id: 'width', label: 'Width', hint: 'e.g. 1 inch, 1.5 inch' },
      { id: 'buckleStyle', label: 'Buckle Style', hint: 'classic pin, ratchet, box frame, western' },
      { id: 'size', label: 'Size', hint: 'e.g. 34, 36, 38' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Fossil, Coach, Levi\'s' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $80' },
    ],
  },
  'bags': {
    label: 'Bag',
    required: ['bagType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'bagType', label: 'Bag Type', hint: 'tote, backpack, crossbody, clutch, duffel, briefcase' },
      { id: 'material', label: 'Material', hint: 'leather, canvas, nylon, vegan leather' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Coach, Michael Kors, Herschel' },
      { id: 'color', label: 'Color', hint: 'e.g. black, tan, navy' },
      { id: 'sizeCapacity', label: 'Size / Capacity', hint: 'e.g. small, medium, large, 20L' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $150' },
    ],
  },
  'wallets': {
    label: 'Wallet',
    required: ['walletStyle', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'walletStyle', label: 'Wallet Style', hint: 'bifold, trifold, slim card holder, money clip, zip-around' },
      { id: 'material', label: 'Material', hint: 'leather, canvas, vegan leather, metal' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Bellroy, Fossil, Louis Vuitton' },
      { id: 'cardSlots', label: 'Card Slots', hint: 'e.g. 4, 6, 8+' },
      { id: 'color', label: 'Color', hint: 'e.g. black, brown, tan' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $60' },
    ],
  },
  'jewelry-rings': {
    label: 'Jewelry — Rings',
    required: ['materialPreference', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'materialPreference', label: 'Material / Metal Preference', hint: 'white gold, yellow gold, rose gold, platinum, sterling silver' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, sapphire, emerald, ruby, no stone' },
      { id: 'style', label: 'Style / Aesthetic', hint: 'solitaire, halo, eternity, stackable, cocktail, minimalist, vintage' },
      { id: 'size', label: 'Ring Size', hint: 'e.g. size 6, size 7, size 8' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Tiffany, Pandora, Kay Jewelers, local jeweler' },
      { id: 'sourcingPreference', label: 'Sourcing Preference', hint: 'lab-grown, natural/mined, recycled/ethical, handmade, no preference' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,000' },
    ],
  },
  'jewelry-earrings': {
    label: 'Jewelry — Earrings',
    required: ['materialPreference', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'materialPreference', label: 'Material / Metal Preference', hint: 'white gold, yellow gold, rose gold, platinum, sterling silver' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, pearl, sapphire, emerald, no stone' },
      { id: 'style', label: 'Style / Aesthetic', hint: 'studs, hoops, drop, dangle, huggie, ear cuff, minimalist, statement' },
      { id: 'size', label: 'Earring Size / Drop Length', hint: 'e.g. small studs, 1 inch drop, 40mm hoop diameter' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Tiffany, Zales, James Allen, Mejuri, local jeweler' },
      { id: 'sourcingPreference', label: 'Sourcing Preference', hint: 'lab-grown, natural/mined, recycled/ethical, handmade, no preference' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $300' },
    ],
  },
  'jewelry-bracelets': {
    label: 'Jewelry — Bracelets',
    required: ['materialPreference', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'materialPreference', label: 'Material / Metal Preference', hint: 'white gold, yellow gold, rose gold, platinum, sterling silver' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, gemstone, pearl, no stone' },
      { id: 'style', label: 'Style / Aesthetic', hint: 'tennis, bangle, charm, cuff, chain, beaded, minimalist, statement' },
      { id: 'size', label: 'Bracelet Length / Size', hint: 'e.g. 6.5 inch, 7 inch, 7.5 inch wrist' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Pandora, Gorjana, Kendra Scott, local jeweler' },
      { id: 'sourcingPreference', label: 'Sourcing Preference', hint: 'lab-grown, natural/mined, recycled/ethical, handmade, no preference' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
    ],
  },
  'jewelry-necklaces': {
    label: 'Jewelry — Necklaces',
    required: ['materialPreference', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'materialPreference', label: 'Material / Metal Preference', hint: 'white gold, yellow gold, rose gold, platinum, sterling silver' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, pearl, gemstone, no stone' },
      { id: 'style', label: 'Style / Aesthetic', hint: 'chain, choker, layered, statement, lariat, pendant, minimalist' },
      { id: 'size', label: 'Necklace Length', hint: 'e.g. 14 inch choker, 16 inch, 18 inch, 20 inch, 24 inch' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Tiffany, Zales, James Allen, Mejuri, local jeweler' },
      { id: 'sourcingPreference', label: 'Sourcing Preference', hint: 'lab-grown, natural/mined, recycled/ethical, handmade, no preference' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $400' },
    ],
  },
  'jewelry-pendants': {
    label: 'Jewelry — Pendants',
    required: ['materialPreference', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'materialPreference', label: 'Material / Metal Preference', hint: 'white gold, yellow gold, rose gold, platinum, sterling silver' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, birthstone, gemstone, no stone' },
      { id: 'style', label: 'Style / Aesthetic', hint: 'solitaire, cross, heart, initial, custom, nature-inspired, geometric' },
      { id: 'size', label: 'Chain Length', hint: 'e.g. 16 inch, 18 inch, 20 inch chain; pendant size small/medium/large' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Tiffany, Zales, James Allen, local jeweler' },
      { id: 'sourcingPreference', label: 'Sourcing Preference', hint: 'lab-grown, natural/mined, recycled/ethical, handmade, no preference' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $350' },
    ],
  },
  'jewelry-anklets': {
    label: 'Jewelry — Anklets',
    required: ['materialPreference', 'stoneType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'sex', label: 'Sex', type: 'select', options: ['Male', 'Female', 'Unisex'], hint: 'Male, Female, Unisex' },
      { id: 'materialPreference', label: 'Material / Metal Preference', hint: 'white gold, yellow gold, rose gold, sterling silver, gold-filled' },
      { id: 'stoneType', label: 'Stone Type', hint: 'diamond, gemstone, charm, no stone' },
      { id: 'style', label: 'Style / Aesthetic', hint: 'chain, beaded, charm, layered, adjustable, minimalist, boho' },
      { id: 'size', label: 'Anklet Size', hint: 'e.g. 9 inch, 10 inch, adjustable 9–11 inch' },
      { id: 'brand', label: 'Brand', hint: 'e.g. Pandora, Gorjana, Kendra Scott, local jeweler' },
      { id: 'sourcingPreference', label: 'Sourcing Preference', hint: 'lab-grown, natural/mined, recycled/ethical, handmade, no preference' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $150' },
    ],
  },

  // ── Other (generic) schemas per category ─────────────────────────────────
  'other-home-property': {
    label: 'Other — Home & Property',
    required: ['projectDescription', 'priceMax'],
    fields: [
      { id: 'projectDescription', label: 'Project Description', hint: 'Describe what you need done' },
      { id: 'location', label: 'Location / Area', hint: 'e.g. backyard, kitchen, whole house' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $5,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. within 4 weeks, flexible' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any other details vendors should know' },
    ],
  },
  'other-travel-experiences': {
    label: 'Other — Travel & Experiences',
    required: ['experienceDescription', 'priceMax'],
    fields: [
      { id: 'experienceDescription', label: 'Experience Description', hint: 'Describe what you are looking for' },
      { id: 'destination', label: 'Destination / Location', hint: 'e.g. Europe, local, online' },
      { id: 'numberOfPeople', label: 'Number of People', hint: 'e.g. 2 adults, family of 4' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $2,000' },
      { id: 'preferredDates', label: 'Preferred Dates / Timeframe', hint: 'e.g. June 2025, flexible' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any preferences or requirements' },
    ],
  },
  'other-vehicle-transportation': {
    label: 'Other — Vehicle & Auto',
    required: ['serviceDescription', 'priceMax'],
    fields: [
      { id: 'serviceDescription', label: 'Service / Purchase Description', hint: 'Describe what you need' },
      { id: 'vehicleInfo', label: 'Vehicle Info', hint: 'e.g. 2019 Honda Civic, make/model/year' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,500' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. ASAP, within 2 weeks' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any other details vendors should know' },
    ],
  },
  'other-tech-electronics': {
    label: 'Other — Tech & Electronics',
    required: ['itemDescription', 'priceMax'],
    fields: [
      { id: 'itemDescription', label: 'Item / Service Description', hint: 'Describe what you are looking for' },
      { id: 'useCase', label: 'Use Case', hint: 'e.g. work, gaming, home entertainment' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. Apple, Samsung, no preference' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any features or requirements' },
    ],
  },
  'other-financial-professional': {
    label: 'Other — Finance',
    required: ['serviceDescription', 'priceMax'],
    fields: [
      { id: 'serviceDescription', label: 'Service Description', hint: 'Describe the financial service you need' },
      { id: 'goal', label: 'Goal / Outcome', hint: 'e.g. reduce taxes, plan for retirement' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,000' },
      { id: 'timeline', label: 'Timeline', hint: 'e.g. ongoing, one-time, within 30 days' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any other context for providers' },
    ],
  },
  'other-life-events': {
    label: 'Other — Life Events',
    required: ['eventDescription', 'priceMax'],
    fields: [
      { id: 'eventDescription', label: 'Event / Occasion Description', hint: 'Describe the event or milestone' },
      { id: 'numberOfGuests', label: 'Number of Guests / People', hint: 'e.g. 50 guests, just us' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $3,000' },
      { id: 'eventDate', label: 'Event Date / Timeframe', hint: 'e.g. September 2025, flexible' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any special requirements or preferences' },
    ],
  },
  'other-insurance': {
    label: 'Other — Insurance',
    required: ['coverageDescription', 'priceMax'],
    fields: [
      { id: 'coverageDescription', label: 'Coverage Needed', hint: 'Describe what you want to insure' },
      { id: 'coverageAmount', label: 'Coverage Amount', hint: 'e.g. $500,000, not sure yet' },
      { id: 'priceMax', label: 'Max Monthly Premium (not shared with vendors)', hint: 'e.g. $100/month' },
      { id: 'currentProvider', label: 'Current Provider', hint: 'e.g. State Farm, none currently' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any specific coverage requirements' },
    ],
  },
  'other-home-purchases': {
    label: 'Other — Home Goods',
    required: ['itemDescription', 'priceMax'],
    fields: [
      { id: 'itemDescription', label: 'Item Description', hint: 'Describe what you are looking to buy' },
      { id: 'dimensions', label: 'Size / Dimensions', hint: 'e.g. 60 inch wide, fits in 10x12 room' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $800' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. no preference, prefer American-made' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Color, style, or other preferences' },
    ],
  },
  'lighting': {
    label: 'Lighting',
    required: ['lightingType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'lightingType', label: 'Lighting Type', hint: 'e.g. ceiling, floor lamp, pendant, sconce, under-cabinet' },
      { id: 'roomApplication', label: 'Room / Application', hint: 'e.g. living room, bedroom, kitchen, outdoor' },
      { id: 'stylePreference', label: 'Style Preference', hint: 'e.g. modern, bohemian, minimalist, traditional' },
      { id: 'bulbType', label: 'Bulb Type', hint: 'e.g. LED, incandescent, smart bulb compatible' },
      { id: 'smartCompatibility', label: 'Smart Home Compatibility', hint: 'e.g. Alexa, Google Home, app-controlled' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $300' },
    ],
  },
  'rugs': {
    label: 'Rugs',
    required: ['rugSize', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'rugSize', label: 'Rug Size', hint: 'e.g. 2x3, 5x8, 8x10, runner, or custom dimensions' },
      { id: 'rugMaterial', label: 'Material', hint: 'e.g. wool, cotton, synthetic, jute, silk' },
      { id: 'stylePreference', label: 'Style / Pattern', hint: 'e.g. solid, geometric, traditional, bohemian' },
      { id: 'roomApplication', label: 'Room / Placement', hint: 'e.g. living room, bedroom, entryway, outdoor' },
      { id: 'pile', label: 'Pile Height', hint: 'e.g. low pile, high pile, or shag' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $500' },
    ],
  },
  'decor': {
    label: 'Decor',
    required: ['decorType', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'decorType', label: 'Decor Type', hint: 'e.g. wall art, vases, candles, throw pillows, mirrors' },
      { id: 'stylePreference', label: 'Style Preference', hint: 'e.g. modern, bohemian, minimalist, traditional' },
      { id: 'roomApplication', label: 'Room / Space', hint: 'e.g. living room, bedroom, bathroom, entryway' },
      { id: 'colorScheme', label: 'Color Scheme', hint: 'e.g. neutral tones, bold accent colors' },
      { id: 'materialPreference', label: 'Material Preference', hint: 'e.g. wood, metal, ceramic, fabric, glass' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $200' },
    ],
  },
  'other-education': {
    label: 'Other — Education',
    required: ['programDescription', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'programDescription', label: 'Program / Course Description', hint: 'Describe what you want to learn or achieve' },
      { id: 'format', label: 'Format', hint: 'e.g. online, in-person, self-paced, live sessions' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $1,500' },
      { id: 'timeline', label: 'Start Date / Timeline', hint: 'e.g. ASAP, fall semester, flexible' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any prerequisites, goals, or requirements' },
    ],
  },
  'other-health-wellness': {
    label: 'Other — Health & Wellness',
    required: ['serviceDescription', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'serviceDescription', label: 'Service / Treatment Description', hint: 'Describe what you are looking for' },
      { id: 'goal', label: 'Health Goal', hint: 'e.g. lose weight, manage stress, recover from injury' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $200/month' },
      { id: 'frequency', label: 'Frequency', hint: 'e.g. weekly, monthly, one-time' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Any medical history or preferences relevant to providers' },
    ],
  },
  'other-accessories': {
    label: 'Other — Accessories',
    required: ['itemDescription', 'priceMax'],
    fields: [
      { id: 'condition', label: 'Condition', type: 'select', options: ['New', 'Used', 'Refurbished'], hint: 'New, Used, Refurbished' },
      { id: 'itemDescription', label: 'Item Description', hint: 'Describe the accessory you are looking for' },
      { id: 'materialPreference', label: 'Material / Style Preference', hint: 'e.g. leather, gold, minimalist' },
      { id: 'priceMax', label: 'Max Budget (not shared with vendors)', hint: 'e.g. $250' },
      { id: 'brandPreference', label: 'Brand Preference', hint: 'e.g. no preference, luxury only' },
      { id: 'additionalNotes', label: 'Additional Notes', hint: 'Color, size, or other preferences' },
    ],
  },
};

export default FIELD_SCHEMAS;
